import {
  forwardRef,
  useMemo,
  useState,
  type ReactElement,
  type Ref,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Slide,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import CheckIcon from "@mui/icons-material/Check";
import LanguageIcon from "@mui/icons-material/Language";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import { useSelfSetupTour } from "../../hooks/useSelfSetupTour";
import { APP_LANGUAGES, setAppLanguage, type AppLanguage } from "../../i18n/app";
import { SetupStepsProgress } from "./SetupStepsProgress";
import { APP_FONT_FAMILY } from "../../constants/fonts";

const SlideDown = forwardRef(function SlideDown(
  props: TransitionProps & { children: ReactElement },
  ref: Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const TITLE_SX = {
  fontFamily: APP_FONT_FAMILY,
  fontWeight: 600,
  color: "text.primary",
  lineHeight: 1.235,
  letterSpacing: "0.00735em",
} as const;

/**
 * Welcome / congrats dialog — visual match for Zero SelfSetupGate,
 * using ESG theme primary / success colors.
 */
export function SelfSetupGate() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const {
    showWelcomeHub,
    showCongrats,
    steps,
    nextIncomplete,
    allDone,
    startTour,
    goToStep,
    dismissTour,
    dismissCongrats,
  } = useSelfSetupTour();
  const [languageMenuAnchor, setLanguageMenuAnchor] =
    useState<HTMLElement | null>(null);

  const accent = theme.palette.primary.main;
  const success = theme.palette.success.main;
  const isDark = theme.palette.mode === "dark";
  const completedCount = steps.filter((s) => s.done).length;
  const showRestoreCopy = completedCount > 0 && !allDone;

  const welcomeOpen = Boolean(showWelcomeHub && !allDone && nextIncomplete);
  const congratsOpen = Boolean(showCongrats);
  const dialogOpen = welcomeOpen || congratsOpen;

  const continueLabel = useMemo(() => {
    if (!nextIncomplete) {
      return t("selfSetup.welcomeContinueAction", {
        step: t("selfSetup.steps.dashboard"),
      });
    }
    return t("selfSetup.welcomeContinueAction", {
      step: t(`selfSetup.steps.${nextIncomplete}`),
    });
  }, [nextIncomplete, t]);

  const heroGradient = congratsOpen
    ? isDark
      ? `linear-gradient(155deg, ${alpha(accent, 0.4)} 0%, ${alpha(accent, 0.14)} 48%, ${alpha(accent, 0.04)} 100%)`
      : `linear-gradient(155deg, ${alpha(accent, 0.24)} 0%, ${alpha(accent, 0.09)} 52%, ${alpha(accent, 0.03)} 100%)`
    : isDark
      ? `linear-gradient(155deg, ${alpha(accent, 0.34)} 0%, ${alpha(accent, 0.1)} 48%, ${alpha(success, 0.08)} 100%)`
      : `linear-gradient(155deg, ${alpha(accent, 0.18)} 0%, ${alpha(accent, 0.06)} 52%, ${alpha(success, 0.07)} 100%)`;

  const paperSx = {
    m: 0,
    width: "100%",
    border: 1,
    borderColor: isDark
      ? alpha(theme.palette.common.white, 0.1)
      : alpha(theme.palette.common.black, 0.08),
    boxShadow: isDark
      ? `0 24px 64px ${alpha("#000", 0.55)}`
      : `0 20px 56px ${alpha("#0f172a", 0.14)}`,
    overflow: "hidden",
    position: "relative" as const,
    borderRadius: "14px",
    bgcolor: theme.palette.background.paper,
  };

  const dialogContainerSx = {
    "& .MuiDialog-container": {
      alignItems: "center",
      justifyContent: "center",
      px: 1.5,
    },
  };

  const backdropSx = {
    bgcolor: isDark
      ? alpha(theme.palette.common.black, 0.62)
      : alpha("#0f172a", 0.32),
  };

  const badgeColor = accent;

  const iconBadgeSx = {
    width: 80,
    height: 80,
    mx: "auto",
    mt: -5,
    mb: 2.5,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    bgcolor: isDark
      ? alpha(theme.palette.background.paper, 0.95)
      : theme.palette.background.paper,
    border: `2px solid ${alpha(badgeColor, isDark ? 0.45 : 0.28)}`,
    boxShadow: isDark
      ? `0 10px 28px ${alpha("#000", 0.45)}, 0 0 0 6px ${alpha(badgeColor, 0.12)}`
      : `0 10px 28px ${alpha(badgeColor, 0.18)}, 0 0 0 6px ${alpha(badgeColor, 0.08)}`,
    color: badgeColor,
  };

  const handleLanguageSelect = (language: AppLanguage) => {
    setAppLanguage(language);
    setLanguageMenuAnchor(null);
  };

  return (
    <Dialog
      open={dialogOpen}
      disableEscapeKeyDown
      onClose={() => {
        /* Close only via Later / primary CTA — no X or backdrop dismiss */
      }}
      TransitionComponent={SlideDown}
      keepMounted={false}
      maxWidth={false}
      slotProps={{
        backdrop: {
          sx: backdropSx,
        },
      }}
      sx={dialogContainerSx}
      PaperProps={{
        sx: { ...paperSx, maxWidth: 600 },
      }}
    >
      {welcomeOpen ? (
        <Box
          sx={{
            position: "absolute",
            top: 10,
            left: 10,
            zIndex: 2,
          }}
        >
          <IconButton
            color="primary"
            onClick={(e) => setLanguageMenuAnchor(e.currentTarget)}
            aria-label={t("selfSetup.welcomeLanguageAria")}
            aria-haspopup="menu"
            aria-expanded={Boolean(languageMenuAnchor)}
            sx={{
              width: 40,
              height: 40,
              bgcolor: theme.palette.background.paper,
              color: accent,
              boxShadow: isDark
                ? `0 6px 16px ${alpha("#000", 0.35)}`
                : `0 6px 16px ${alpha("#0f172a", 0.18)}`,
              "&:hover": {
                bgcolor: theme.palette.background.paper,
                color: theme.palette.primary.dark,
              },
            }}
          >
            <LanguageIcon sx={{ fontSize: 22 }} />
          </IconButton>
          <Menu
            anchorEl={languageMenuAnchor}
            open={Boolean(languageMenuAnchor)}
            onClose={() => setLanguageMenuAnchor(null)}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
            transformOrigin={{ horizontal: "left", vertical: "top" }}
            PaperProps={{
              sx: {
                bgcolor: theme.palette.background.paper,
                boxShadow: theme.shadows[3],
                borderRadius: "12px",
                minWidth: 240,
                maxHeight: 420,
                py: 0.5,
                mt: 0.75,
              },
            }}
            MenuListProps={{ dense: true }}
          >
            <Typography
              variant="overline"
              sx={{
                display: "block",
                px: 2,
                py: 0.5,
                color: "text.secondary",
                fontWeight: 700,
                letterSpacing: "0.08em",
              }}
            >
              {t("account.languages")}
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            {APP_LANGUAGES.map((language) => {
              const isActive = i18n.language.startsWith(language.code);
              const localizedName = t(
                `languages.${language.englishLabel.toLowerCase()}`,
                language.englishLabel,
              );
              const showSecondary = localizedName !== language.nativeLabel;
              return (
                <MenuItem
                  key={language.code}
                  selected={isActive}
                  onClick={() => handleLanguageSelect(language.code)}
                  dir="ltr"
                  sx={{
                    gap: 1.25,
                    minHeight: 48,
                    py: 0.75,
                    px: 1.25,
                    mx: 0.75,
                    borderRadius: "10px",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": { bgcolor: `${accent}10` },
                    "&.Mui-selected": {
                      bgcolor: `${accent}14`,
                      "&:hover": { bgcolor: `${accent}1F` },
                    },
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      width: 30,
                      height: 30,
                      flexShrink: 0,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      letterSpacing: "0.02em",
                      bgcolor: isActive
                        ? accent
                        : theme.palette.action.hover,
                      color: isActive
                        ? theme.palette.primary.contrastText
                        : theme.palette.text.secondary,
                    }}
                  >
                    {language.code.toUpperCase()}
                  </Box>
                  <ListItemText
                    primary={language.nativeLabel}
                    secondary={showSecondary ? localizedName : undefined}
                    primaryTypographyProps={{
                      sx: {
                        fontFamily: APP_FONT_FAMILY,
                        fontSize: "0.95rem",
                        fontWeight: isActive ? 700 : 500,
                        lineHeight: 1.35,
                        textAlign: "start",
                      },
                      dir: "ltr",
                    }}
                    secondaryTypographyProps={{
                      sx: {
                        fontFamily: APP_FONT_FAMILY,
                        fontSize: "0.75rem",
                        textAlign: "start",
                      },
                      dir: "ltr",
                    }}
                  />
                  {isActive ? (
                    <CheckIcon fontSize="small" color="primary" />
                  ) : null}
                </MenuItem>
              );
            })}
          </Menu>
        </Box>
      ) : null}

      <Box
        aria-hidden
        sx={{
          height: 100,
          background: heroGradient,
        }}
      />

      <Box
        sx={{
          position: "relative",
          px: { xs: 3.5, sm: 5 },
          pb: 4.5,
          textAlign: "center",
        }}
      >
        <Box sx={iconBadgeSx}>
          {congratsOpen ? (
            <CelebrationOutlinedIcon sx={{ fontSize: 38 }} />
          ) : (
            <RocketLaunchOutlinedIcon sx={{ fontSize: 38 }} />
          )}
        </Box>

        <Typography
          variant="h4"
          sx={{
            ...TITLE_SX,
            mb: congratsOpen ? 3.25 : 1,
            fontSize: { xs: "1.45rem", sm: "1.65rem" },
          }}
        >
          {congratsOpen
            ? t("selfSetup.congratsTitle")
            : t(
                showRestoreCopy
                  ? "selfSetup.welcomeRestoreTitle"
                  : "selfSetup.welcomeTitle",
              )}
        </Typography>

        {!congratsOpen ? (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontFamily: APP_FONT_FAMILY,
              mb: 3.25,
              fontSize: "1rem",
              lineHeight: 1.6,
              maxWidth: 420,
              mx: "auto",
            }}
          >
            {t(
              showRestoreCopy
                ? "selfSetup.welcomeRestoreSubtitle"
                : "selfSetup.welcomeSubtitle",
            )}
          </Typography>
        ) : null}

        <SetupStepsProgress
          steps={steps}
          accent={accent}
          labelFor={(key) => t(`selfSetup.steps.${key}`)}
          highlightCurrent={!congratsOpen}
          onStepClick={
            congratsOpen
              ? undefined
              : (key) => {
                  if (!steps.find((s) => s.key === key)?.done) {
                    goToStep(key);
                  }
                }
          }
        />

        {congratsOpen ? (
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={dismissCongrats}
            sx={{
              fontFamily: APP_FONT_FAMILY,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              py: 1.5,
              fontSize: "1rem",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
                filter: isDark ? "brightness(1.08)" : "brightness(0.97)",
              },
            }}
          >
            {t("selfSetup.congratsAction")}
          </Button>
        ) : nextIncomplete ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.25,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={startTour}
              sx={{
                fontFamily: APP_FONT_FAMILY,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                py: 1.5,
                fontSize: "1rem",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                  filter: isDark ? "brightness(1.08)" : "brightness(0.97)",
                },
              }}
            >
              {continueLabel}
            </Button>
            <Button
              variant="text"
              color="inherit"
              onClick={() => dismissTour()}
              sx={{
                fontFamily: APP_FONT_FAMILY,
                textTransform: "none",
                color: "text.secondary",
                fontWeight: 500,
                fontSize: "0.9rem",
                "&:hover": {
                  bgcolor: isDark
                    ? alpha(theme.palette.common.white, 0.06)
                    : alpha(theme.palette.common.black, 0.04),
                  color: "text.primary",
                },
              }}
            >
              {t("selfSetup.welcomeLaterAction")}
            </Button>
          </Box>
        ) : null}
      </Box>
    </Dialog>
  );
}

export default SelfSetupGate;
